import { getSchooljaarTovHuidig } from './date.utils';

export enum SchooljaarHistorieOption {
	AFGELOPEN_3 = 'Afgelopen 3 schooljaren',
	AFGELOPEN_5 = 'Afgelopen 5 schooljaren',
	AFGELOPEN_10 = 'Afgelopen 10 schooljaren',
	AANGEPAST = 'Aangepast',
}

export type SchooljaarHistorieFilterValue = {
	option: SchooljaarHistorieOption;
	inclHuidig: boolean;
};

/**
 * De schooljaarfilters van een domein: bij de Historie-variant vervangen `historie` en `aangepast` het filter `actueel`.
 */
export type SchooljaarHistorieFilters = {
	actueel: string;
	alias?: string;
	historie: string;
	aangepast: string;
	minimum: number;
};

export const SCHOOLJAAR_HISTORIE_FILTERS: SchooljaarHistorieFilters[] = [
	{ actueel: 'aw_nm_schooljaar', historie: 'x_aw_schooljaar_historie', aangepast: 'x_aw_multiselect_schooljaar', minimum: 1 },
	{ actueel: 'cf_nm_schooljaar', historie: 'x_cijfer_schooljaar_historie', aangepast: 'x_cijfer_multiselect_schooljaar', minimum: 2 },
	{
		actueel: 'ds_nm_schooljaar_van',
		alias: 'x_prestatieanalyse_ds_schooljaar',
		historie: 'x_doorstroom_schooljaar_historie',
		aangepast: 'x_doorstroom_multiselect_schooljaar',
		minimum: 2,
	},
	{ actueel: 'ekc_nm_schooljaar', historie: 'x_ekc_schooljaar_historie', aangepast: 'x_ekc_multiselect_schooljaar', minimum: 2 },
	{ actueel: 'lr_d_datum.per_nm_schooljaar', historie: 'x_lr_schooljaar_historie', aangepast: 'x_lr_multiselect_schooljaar', minimum: 1 },
	{ actueel: 'pv_nm_schooljaar', historie: 'x_pv_schooljaar_historie', aangepast: 'x_pv_multiselect_schooljaar', minimum: 2 },
	{ actueel: 'vkk_nm_schooljaar', historie: 'x_vkk_schooljaar_historie', aangepast: 'x_vkk_multiselect_schooljaar', minimum: 2 },
	{
		actueel: 'x_onderwijsresultaten_or_schooljaar',
		historie: 'x_onderwijsresultaten_or_schooljaar_historie',
		aangepast: 'x_onderwijsresultaten_or_multiselect_schooljaar',
		minimum: 2,
	},
];

/**
 * Bij deze dashboards is het schooljaarfilter één eindschooljaar: het dashboard toont de drie schooljaren die op dat jaar
 * eindigen. De Historie-variant toont die per schooljaar in plaats van gecombineerd.
 */
export const SCHOOLJAAR_INDICATOR_FILTERS = ['x_onderwijsresultaten_ds_schooljaar', 'x_onderwijsresultaten_cf_schooljaar'];

export function minimumSchooljaren(aangepastFilterName: string): number {
	return SCHOOLJAAR_HISTORIE_FILTERS.find((f) => f.aangepast === aangepastFilterName)?.minimum ?? 2;
}

export function isSchooljaarFilterVan(filters: SchooljaarHistorieFilters, filterName: string): boolean {
	return filterName === filters.actueel || filterName === filters.alias;
}

const AANTAL_SCHOOLJAREN: { [option in SchooljaarHistorieOption]?: number } = {
	[SchooljaarHistorieOption.AFGELOPEN_3]: 3,
	[SchooljaarHistorieOption.AFGELOPEN_5]: 5,
	[SchooljaarHistorieOption.AFGELOPEN_10]: 10,
};

/**
 * De schooljaren van een historiefilter, of `undefined` bij `Aangepast`.
 */
export function schooljarenVoorHistorie({ option, inclHuidig }: SchooljaarHistorieFilterValue): string[] | undefined {
	const aantal = AANTAL_SCHOOLJAREN[option];
	if (!aantal) return undefined;

	const laatste = inclHuidig ? 0 : -1;
	return Array.from({ length: aantal }, (_, i) => getSchooljaarTovHuidig(laatste - aantal + 1 + i));
}

/**
 * De historiefilterwaarde bij deze schooljaren: een standaardperiode als ze er precies een vormen, anders `Aangepast`.
 */
export function historieVoorSchooljaren(schooljaren: string[]): SchooljaarHistorieFilterValue {
	for (const option of Object.values(SchooljaarHistorieOption)) {
		for (const inclHuidig of [true, false]) {
			const kandidaat = schooljarenVoorHistorie({ option, inclHuidig });
			if (kandidaat?.length === schooljaren.length && kandidaat.every((sj, i) => sj === schooljaren[i])) return { option, inclHuidig };
		}
	}

	return { option: SchooljaarHistorieOption.AANGEPAST, inclHuidig: true };
}
